import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ExternalLink, BookOpen } from 'lucide-react';
// Props: publications

const Publications = ({ publications }) => {
  if (!publications) return null;
  const getStatusBadge = (status) => {
    if (status === 'published') {
      return { color: 'bg-green-100 text-green-800 border-green-200', label: 'Published' };
    }
    return { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'In Preparation' };
  };

  return (
    <section id="publications" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Publications</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Peer-reviewed research contributions and manuscripts in preparation
          </p>
          <div className="w-20 h-1 bg-blue-900 mx-auto mt-6"></div>
        </div>

        <div className="space-y-6">
          {publications.map((pub) => {
            const statusBadge = getStatusBadge(pub.status);
            return (
              <Card
                key={pub.id}
                className={`border-2 transition-all hover:shadow-lg ${
                  pub.status === 'published'
                    ? 'border-green-100 hover:border-green-200 bg-green-50/30'
                    : 'border-gray-100 hover:border-blue-200'
                }`}
              >
                <CardHeader>
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <BookOpen className="w-5 h-5 text-blue-900" />
                        <Badge className={`${statusBadge.color} border`}>
                          {statusBadge.label}
                        </Badge>
                        <Badge variant="outline" className="bg-slate-50">
                          {pub.type}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl lg:text-2xl mb-2">
                        {pub.title}
                      </CardTitle>
                      <div className="text-gray-600 space-y-1">
                        <p className="font-medium">{pub.authors}</p>
                        <p className="text-sm">
                          <span className="italic">{pub.venue}</span>, {pub.year}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4 leading-relaxed">{pub.abstract}</p>
                  
                  {pub.status === 'published' && pub.doi && (
                    <div className="bg-green-100 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-900">
                        <strong>DOI:</strong> {pub.doi}
                      </p>
                    </div>
                  )}

                  {pub.doiLink && (
                    <Button
                      onClick={() => window.open(pub.doiLink, '_blank')}
                      className="bg-blue-900 hover:bg-blue-800"
                      size="sm"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Publication
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Publication Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <Card className="border-2 border-green-100 bg-green-50/30">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-green-800 mb-2">
                {publications.filter(p => p.status === 'published').length}
              </div>
              <div className="text-gray-700 font-medium">Published Papers</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-amber-100 bg-amber-50/30">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-amber-800 mb-2">
                {publications.filter(p => p.status === 'in-preparation').length}
              </div>
              <div className="text-gray-700 font-medium">Manuscripts in Preparation</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-100 bg-blue-50/30">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-blue-800 mb-2">
                {publications.length}
              </div>
              <div className="text-gray-700 font-medium">Total Research Outputs</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Publications;
